<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  options: {
    type: Array,
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Selecione...',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String,
    default: 'bottom-start',
    validator: v =>
      [
        'bottom-start',
        'bottom-end',
        'top-start',
        'top-end',
        'bottom',
        'top',
      ].includes(v),
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const open = ref(false);
const pillRef = ref(null);

// ── Derived ──────────────────────────────────────────────────────────────────

const selectedOption = computed(
  () => props.options.find(o => o.value === props.modelValue) ?? null
);

const pillLabel = computed(
  () => selectedOption.value?.label ?? props.placeholder
);

const pillColorClass = computed(
  () => selectedOption.value?.colorClass ?? 'bg-n-alpha-1 text-n-slate-11'
);

// ── Popover position ─────────────────────────────────────────────────────────

const popoverClass = computed(() => {
  const map = {
    'bottom-start': 'top-full left-0 mt-1',
    'bottom-end': 'top-full right-0 mt-1',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-1',
    'top-start': 'bottom-full left-0 mb-1',
    'top-end': 'bottom-full right-0 mb-1',
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-1',
  };
  return map[props.placement] ?? map['bottom-start'];
});

// ── Interactions ─────────────────────────────────────────────────────────────

const toggleOpen = () => {
  if (props.disabled) return;
  open.value = !open.value;
};

const selectOption = opt => {
  const prev = props.modelValue;
  emit('update:modelValue', opt.value);
  open.value = false;
  emit('change', opt.value, prev);
};

const onKeydown = e => {
  if (e.key === 'Escape') open.value = false;
};

// ── Click-outside ────────────────────────────────────────────────────────────

const onDocumentClick = e => {
  if (!open.value) return;
  if (pillRef.value && !pillRef.value.contains(e.target)) {
    open.value = false;
  }
};

onMounted(() => document.addEventListener('click', onDocumentClick, true));
onBeforeUnmount(() =>
  document.removeEventListener('click', onDocumentClick, true)
);

// Expose for testing
defineExpose({ open, onKeydown, selectOption, toggleOpen });
</script>

<template>
  <div ref="pillRef" class="relative inline-flex" @keydown="onKeydown">
    <!-- ── Pill trigger ── -->
    <button
      type="button"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :disabled="disabled"
      class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium cursor-pointer transition-opacity duration-100 disabled:opacity-50 disabled:cursor-default"
      :class="pillColorClass"
      @click.stop="toggleOpen"
    >
      <!-- Icon slot -->
      <span
        v-if="selectedOption && selectedOption.icon"
        class="flex-shrink-0 text-current"
        aria-hidden="true"
      >
        <component
          :is="selectedOption.icon"
          v-if="typeof selectedOption.icon !== 'string'"
        />
        <span v-else>{{ selectedOption.icon }}</span>
      </span>

      <span>{{ pillLabel }}</span>

      <!-- Chevron -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="flex-shrink-0 transition-transform duration-150"
        :class="open ? 'rotate-180' : ''"
        aria-hidden="true"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>

    <!-- ── Popover ── -->
    <div
      v-if="open"
      role="listbox"
      :aria-label="placeholder"
      class="absolute z-50 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg p-1 min-w-[160px] transition-all duration-100"
      :class="popoverClass"
    >
      <button
        v-for="opt in options"
        :key="opt.value"
        role="option"
        type="button"
        :aria-selected="opt.value === modelValue"
        class="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer transition-colors duration-75 text-left"
        :class="opt.value === modelValue ? 'bg-n-alpha-1 font-medium' : ''"
        @click.stop="selectOption(opt)"
      >
        <!-- Option color dot -->
        <span
          v-if="opt.colorClass"
          class="inline-block w-2 h-2 rounded-full flex-shrink-0"
          :class="opt.colorClass"
          aria-hidden="true"
        />

        <!-- Option icon -->
        <span
          v-if="opt.icon && !opt.colorClass"
          class="flex-shrink-0 text-n-slate-11"
          aria-hidden="true"
        >
          {{ opt.icon }}
        </span>

        <span class="flex-1">{{ opt.label }}</span>

        <!-- Checkmark for selected -->
        <svg
          v-if="opt.value === modelValue"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-brand flex-shrink-0"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </button>
    </div>
  </div>
</template>
